import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Hash, BookOpenText, Workflow, MessageSquare } from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../ui/command";
import { DialogTitle, DialogDescription } from "../ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import api from "../../lib/api";

/**
 * Brief 3.4.13.1 — Cmd-K (Ctrl-K on Win/Linux) palette for ops docs.
 *
 * Single global mount in AdminLayout. Keyboard handler listens for
 * the meta/ctrl + K combo; opens a fuzzy-search palette across every
 * heading in every whitelisted doc.
 *
 * On select, navigates to `/admin/docs/manual/{doc_slug}#{anchor}` —
 * relies on react-markdown's `id` attrs on h1-h6 (added in
 * AdminDocsManual via the `components` prop).
 *
 * Index is fetched once on first open + cached for the session
 * (we hot-reload on dev so stale data is rare; if a doc edit
 * matters during a session, refresh the browser).
 */

const ICON_BY_SLUG = {
  "compliance-manual": BookOpenText,
  "telegram-manual": MessageSquare,
  "telegram-status": Workflow,
};

export default function DocsCommandPalette() {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [loadingErr, setLoadingErr] = useState("");
  const navigate = useNavigate();

  // Keyboard listener — Cmd-K / Ctrl-K toggles the palette.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Lazy-fetch the index on first open.
  const ensureLoaded = useCallback(async () => {
    if (entries !== null) return;
    try {
      const { data } = await api.get("/admin/docs/_index");
      setEntries(data.entries || []);
    } catch (e) {
      setLoadingErr(
        e?.response?.data?.detail || "Could not load docs index."
      );
      setEntries([]);
    }
  }, [entries]);

  useEffect(() => { if (open) ensureLoaded(); }, [open, ensureLoaded]);

  const grouped = useMemo(() => {
    if (!entries) return {};
    const g = {};
    for (const e of entries) {
      const key = `${e.doc_slug}|${e.doc_title}`;
      if (!g[key]) g[key] = [];
      g[key].push(e);
    }
    return g;
  }, [entries]);

  const onSelect = (entry) => {
    setOpen(false);
    // Defer navigation a tick so the dialog dismount finishes first
    // (avoids react-router race on focus restore).
    setTimeout(() => {
      navigate(`/admin/docs/manual/${entry.doc_slug}#${entry.anchor}`);
    }, 0);
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      data-testid="docs-command-palette"
    >
      {/* a11y: Radix Dialog requires a Title + Description. We hide
          them visually since the CommandInput placeholder + grouped
          headings already communicate context to sighted users. */}
      <VisuallyHidden>
        <DialogTitle>Search ops docs</DialogTitle>
        <DialogDescription>
          Fuzzy search across every heading in the Compliance manual
          and Telegram status docs. Type a keyword, then press Enter
          to jump to that section.
        </DialogDescription>
      </VisuallyHidden>
      <CommandInput
        placeholder="Search ops docs — type a heading, e.g. 'screening' or 'approve'…"
        data-testid="docs-command-palette-input"
      />
      <CommandList data-testid="docs-command-palette-list">
        <CommandEmpty>
          {loadingErr || (entries === null ? "Loading…" : "No matches.")}
        </CommandEmpty>
        {Object.entries(grouped).map(([key, items]) => {
          const [slug, title] = key.split("|");
          const Icon = ICON_BY_SLUG[slug] || Hash;
          return (
            <CommandGroup key={key} heading={title}>
              {items.map((entry, i) => (
                <CommandItem
                  key={`${slug}-${entry.anchor}-${i}`}
                  value={`${title} ${entry.breadcrumb} ${entry.text}`}
                  onSelect={() => onSelect(entry)}
                  data-testid={`docs-command-palette-item-${slug}-${entry.anchor}`}
                  className="cursor-pointer"
                >
                  <Icon className="mr-2 h-3.5 w-3.5 text-cyan-300/70" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-[12.5px] text-zinc-100 truncate">
                      {entry.text}
                    </span>
                    {entry.breadcrumb && entry.breadcrumb !== title && (
                      <span className="text-[10.5px] text-zinc-500 font-mono truncate">
                        {entry.breadcrumb}
                      </span>
                    )}
                  </div>
                  <span className="ml-auto rounded border border-white/[0.08] bg-black/30 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-zinc-500 font-mono">
                    H{entry.level}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
}
